import Header from "@/components/site/Header";
import Ticker from "@/components/site/Ticker";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";

export default function PrivacyPolicyPage() {
  return (
    <div data-testid="privacy-page" className="bg-white text-[#0a0a0a]">
      <Seo
        title="Privacy Policy — Universtrend"
        description="How Universtrend collects, uses, and protects your data — including cookies, advertising, analytics, and your GDPR rights."
      />
      <Header />
      <Ticker />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10 py-16 lg:py-20">
          <div className="overline text-[#52525b] mb-4">Legal</div>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-4xl lg:text-6xl">
            Privacy Policy
          </h1>
          <p className="font-mono text-xs text-[#52525b] mt-4">Last updated: 06 July 2026</p>

          <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-[#0a0a0a]">
            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-2">1. Who we are</h2>
              <p>
                Universtrend (&ldquo;we&rdquo;, &ldquo;us&rdquo;, this site) is an independent editorial project available at
                <a href="https://universtrend.com" className="underline"> universtrend.com</a>. For any privacy question, contact{" "}
                <a href="mailto:jdemaratte@gmail.com" className="underline hover:text-[#ff3b30]">jdemaratte@gmail.com</a>.
              </p>
            </div>

            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-2">2. What we collect</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><b>Email address</b> — only if you voluntarily subscribe to our newsletter or send us a sponsor request.</li>
                <li><b>Payment data</b> — if you buy a sponsor slot, payment is processed by Stripe. We never see or store your card details.</li>
                <li><b>Anonymised technical data</b> — pages visited, browser type, approximate country, and IP address (temporarily, in server logs).</li>
                <li><b>Aggregate click data</b> — which affiliate cards get clicked, without linking clicks to individuals.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-2">3. Cookies</h2>
              <p>We use a small number of cookies:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li><b>Essential</b> — a session cookie for the private owner dashboard. This cookie is not used for tracking visitors.</li>
                <li><b>Advertising</b> — Google AdSense may set cookies to serve relevant ads and measure performance. You can control this at{" "}
                  <a href="https://adssettings.google.com" className="underline" target="_blank" rel="noreferrer">adssettings.google.com</a>.
                </li>
                <li><b>Analytics</b> — anonymous product analytics via PostHog (event counts and page views, no personal identifiers).</li>
              </ul>
            </div>

            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-2">4. Third parties we use</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><b>Google AdSense</b> — to display ads. See{" "}
                  <a href="https://policies.google.com/technologies/ads" className="underline" target="_blank" rel="noreferrer">Google&apos;s advertising policy</a>.
                </li>
                <li><b>Stripe</b> — to process sponsor payments. See{" "}
                  <a href="https://stripe.com/privacy" className="underline" target="_blank" rel="noreferrer">Stripe&apos;s privacy policy</a>.
                </li>
                <li><b>Cloudflare</b> — for delivery, security, and DDoS protection.</li>
                <li><b>Emergent</b> — hosting infrastructure.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-2">5. Advertising &amp; personalised content</h2>
              <p>
                Google, as a third-party vendor, uses cookies to serve ads on our site. Google&apos;s use of the DoubleClick cookie enables it and its partners to serve ads based on your visits to this and other sites. You may opt out of the DoubleClick cookie by visiting{" "}
                <a href="https://policies.google.com/technologies/ads" className="underline" target="_blank" rel="noreferrer">Google Ads Settings</a>.
              </p>
            </div>

            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-2">6. Your rights (GDPR / CCPA)</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Access the personal data we hold about you.</li>
                <li>Ask us to correct or delete it.</li>
                <li>Unsubscribe from the newsletter at any time (one-click).</li>
                <li>Withdraw consent to advertising cookies via Google Ads Settings.</li>
              </ul>
              <p className="mt-3">
                To exercise any of these, email{" "}
                <a href="mailto:jdemaratte@gmail.com" className="underline hover:text-[#ff3b30]">jdemaratte@gmail.com</a>. We reply within 30 days.
              </p>
            </div>

            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-2">7. Data retention</h2>
              <p>
                Newsletter emails are stored until you unsubscribe. Sponsor transaction data is retained for the legal minimum (10 years in most jurisdictions). Server access logs are rotated every 30 days.
              </p>
            </div>

            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-2">8. Children</h2>
              <p>
                Universtrend is not directed at children under 13. We do not knowingly collect data from children. If you believe a child has provided us with personal data, contact us and we will remove it.
              </p>
            </div>

            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-2">9. Changes to this policy</h2>
              <p>
                We may update this policy occasionally. Any material change will be flagged with a new &ldquo;Last updated&rdquo; date at the top of this page.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
